import React, { useState, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { getUser, logout } from '../../services/helpers'
import { validateTicket } from '../../services/tickets'
import {
  QrCodeIcon,
  TicketIcon,
  CalendarIcon,
  TagIcon,
  ShoppingBagIcon,
  WalletIcon,
  UserCircleIcon,
  ArrowRightOnRectangleIcon,
  ChartBarIcon,
  Bars3Icon,
  XMarkIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  PhotoIcon,
  MagnifyingGlassIcon,
  ClockIcon,
  MapPinIcon,
  UserIcon
} from '@heroicons/react/24/outline'

const CenterTicketVerificationPage = () => {
  const navigate = useNavigate()
  const [user, setUser] = useState(getUser())
  const displayName = user?.organization || user?.name || 'Event Center'
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [ticketCode, setTicketCode] = useState('')
  const [ticket, setTicket] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [scanMode, setScanMode] = useState(false)
  const videoRef = useRef(null)
  const streamRef = useRef(null)

  const tabs = [
    { id: 'overview', label: 'Overview', icon: ChartBarIcon, path: '/center' },
    { id: 'events', label: 'My Events', icon: CalendarIcon, path: '/center/events', disabled: true },
    { id: 'offers', label: 'My Offers', icon: TagIcon, path: '/center/offers' },
    { id: 'orders', label: 'Orders', icon: ShoppingBagIcon, path: '/center/orders' },
    { id: 'tickets', label: 'Tickets', icon: TicketIcon, path: '/center/tickets' },
    { id: 'verify', label: 'Verify Tickets', icon: QrCodeIcon, path: '/center/verify' },
    { id: 'media', label: 'Media Gallery', icon: PhotoIcon, path: '/center/media', disabled: true },
    { id: 'disputes', label: 'Disputes', icon: ExclamationTriangleIcon, path: '/center/disputes' },
    { id: 'wallet', label: 'Wallet', icon: WalletIcon, path: '/center/wallet' },
    { id: 'profile', label: 'Profile', icon: UserCircleIcon, path: '/center/profile' }
  ]

  const handleVerifyTicket = async (code) => {
    if (!code || code.trim() === '') {
      setError('Please enter a ticket code')
      return
    }

    setLoading(true)
    setError('')
    setSuccess('')
    setTicket(null)

    try {
      // Extract ticket code if it's in the format "TICKET:CODE:ID:TYPE"
      let actualCode = code.trim()
      if (actualCode.startsWith('TICKET:')) {
        const parts = actualCode.split(':')
        if (parts.length > 1) {
          actualCode = parts[1]
        }
      }

      const res = await validateTicket({ code: actualCode })
      
      if (res.status === 200) {
        setTicket(res.data)
        setSuccess('Ticket validated successfully!')
        setTicketCode('')
        if (scanMode) {
          stopScanning()
        }
      } else {
        setError(res.error || 'Failed to validate ticket')
        // If error response includes ticket data (e.g., already used), show it
        if (res.data?.ticket) {
          setTicket(res.data.ticket)
        }
      }
    } catch (err) {
      console.error('Verification error:', err)
      setError('Failed to verify ticket. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    handleVerifyTicket(ticketCode)
  }

  const handleManualEntry = () => {
    setScanMode(false)
    stopScanning()
  }

  const startScanning = async () => {
    setScanMode(true)
    setError('')
    setTicket(null)
    
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'environment' } // Use back camera on mobile
      })
      streamRef.current = stream
      if (videoRef.current) {
        videoRef.current.srcObject = stream
      }
    } catch (err) {
      console.error('Camera error:', err)
      setError('Could not access camera. Please use manual entry.')
      setScanMode(false)
    }
  }

  const stopScanning = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop())
      streamRef.current = null
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null
    }
    setScanMode(false)
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'ACTIVE': return 'text-green-600 bg-green-50 border-green-200'
      case 'USED': return 'text-gray-600 bg-gray-50 border-gray-200'
      case 'TRANSFERRED': return 'text-blue-600 bg-blue-50 border-blue-200'
      case 'VOID': return 'text-red-600 bg-red-50 border-red-200'
      default: return 'text-gray-600 bg-gray-50 border-gray-200'
    }
  }

  const getTicketType = (ticket) => {
    return ticket.eventId ? 'EVENT' : 'OFFER'
  }

  const getTicketPrice = (ticket) => {
    if (ticket.ticketTypeId?.price) {
      return ticket.ticketTypeId.price
    }
    if (ticket.offerId?.discountedPrice) {
      return ticket.offerId.discountedPrice
    }
    return 0
  }

  return (
    <div className="min-h-screen bg-slate-50 flex font-sans">
      {/* Sidebar Navigation */}
      <aside className="hidden lg:block fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-slate-200">
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center gap-3 px-6 h-16 border-b border-slate-100">
            <Link to="/" className="flex items-center gap-2 group">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-600 to-emerald-500 flex items-center justify-center text-white shadow-sm transition-transform group-hover:scale-105">
                <TicketIcon className="w-5 h-5" />
              </div>
              <span className="text-xl font-bold text-slate-900 tracking-tight">Obule</span>
            </Link>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-6 space-y-1 overflow-y-auto">
            {tabs.filter(tab => !tab.disabled).map((tab) => {
              const Icon = tab.icon
              const currentPath = window.location.pathname
              const isActive = currentPath === tab.path || (tab.id === 'verify' && currentPath.startsWith('/center/verify'))
              
              return (
                <Link
                  key={tab.id}
                  to={tab.path}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 ${
                    isActive
                      ? 'bg-emerald-50 text-emerald-700'
                      : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                  }`}
                >
                  <Icon className={`w-5 h-5 ${isActive ? 'text-emerald-600' : 'text-slate-400'}`} />
                  {tab.label}
                </Link>
              )
            })}
          </nav>

          {/* User Profile */}
          <div className="p-4 border-t border-slate-100">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-500">
                <UserCircleIcon className="w-6 h-6" />
              </div>
              <div className="overflow-hidden">
                <p className="text-sm font-semibold text-slate-900 truncate">{displayName}</p>
                <p className="text-xs text-slate-500 truncate">{user?.email}</p>
              </div>
            </div>
            <button
              onClick={() => {
                logout()
                navigate('/center/login')
              }}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors border border-transparent hover:border-red-100"
            >
              <ArrowRightOnRectangleIcon className="w-4 h-4" />
              Sign Out
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 lg:ml-64 min-w-0 flex flex-col">
        {/* Mobile Header */}
        <header className="lg:hidden bg-white border-b border-slate-200 flex items-center justify-between px-4 h-16 sticky top-0 z-40">
          <div className="flex items-center gap-2">
            <TicketIcon className="w-6 h-6 text-emerald-600" />
            <span className="font-semibold text-slate-900">Verify Tickets</span>
          </div>
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="p-2 text-slate-600"
          >
            {mobileMenuOpen ? (
              <XMarkIcon className="w-6 h-6" />
            ) : (
              <Bars3Icon className="w-6 h-6" />
            )}
          </button>
        </header>

        {/* Content */}
        <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-y-auto">
          <div className="max-w-4xl mx-auto">
            {/* Header */}
            <div className="mb-8">
              <h1 className="text-4xl font-bold text-slate-900 mb-2">Verify Tickets</h1>
              <p className="text-slate-600">Scan QR codes or manually enter ticket codes to verify entry</p>
            </div>

            {/* Verification Options */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 mb-6">
              <div className="flex flex-col sm:flex-row gap-4">
                {/* Manual Entry */}
                <div className="flex-1">
                  <form onSubmit={handleSubmit} className="flex gap-3">
                    <div className="flex-1">
                      <input
                        type="text"
                        value={ticketCode}
                        onChange={(e) => setTicketCode(e.target.value)}
                        placeholder="Enter ticket code..."
                        className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                        disabled={loading || scanMode}
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={loading || scanMode || !ticketCode.trim()}
                      className="px-6 py-3 bg-emerald-600 text-white rounded-xl font-semibold hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {loading ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          Verifying...
                        </>
                      ) : (
                        <>
                          <MagnifyingGlassIcon className="w-5 h-5" />
                          Verify
                        </>
                      )}
                    </button>
                  </form>
                </div>

                {/* QR Scanner Toggle */}
                <button
                  onClick={scanMode ? handleManualEntry : startScanning}
                  className={`px-6 py-3 rounded-xl font-semibold transition-colors flex items-center gap-2 ${
                    scanMode
                      ? 'bg-red-600 text-white hover:bg-red-700'
                      : 'bg-purple-600 text-white hover:bg-purple-700'
                  }`}
                  disabled={loading}
                >
                  <QrCodeIcon className="w-5 h-5" />
                  {scanMode ? 'Stop Scanning' : 'Scan QR Code'}
                </button>
              </div>

              {/* QR Scanner Video */}
              {scanMode && (
                <div className="mt-6 relative bg-slate-900 rounded-xl overflow-hidden" style={{ aspectRatio: '16/9' }}>
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="border-4 border-emerald-500 rounded-xl w-64 h-64"></div>
                  </div>
                  <p className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white text-sm bg-black/50 px-4 py-2 rounded-lg">
                    Position QR code within the frame
                  </p>
                </div>
              )}
            </div>

            {/* Messages */}
            {error && (
              <div className="mb-6 p-4 rounded-xl bg-red-50 border border-red-200 text-red-700 flex items-center gap-3">
                <XCircleIcon className="w-5 h-5 shrink-0" />
                <span className="text-sm font-medium">{error}</span>
                <button onClick={() => setError('')} className="ml-auto">
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>
            )}

            {success && (
              <div className="mb-6 p-4 rounded-xl bg-green-50 border border-green-200 text-green-700 flex items-center gap-3">
                <CheckCircleIcon className="w-5 h-5 shrink-0" />
                <span className="text-sm font-medium">{success}</span>
                <button onClick={() => setSuccess('')} className="ml-auto">
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>
            )}

            {/* Ticket Details */}
            {ticket && (
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                {/* Ticket Header */}
                <div className={`p-6 text-white ${
                  getTicketType(ticket) === 'EVENT'
                    ? 'bg-gradient-to-br from-purple-600 to-purple-800'
                    : 'bg-gradient-to-br from-emerald-600 to-emerald-800'
                }`}>
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h2 className="text-2xl font-bold mb-1">
                        {ticket.eventId?.title || ticket.offerId?.title || 'Ticket'}
                      </h2>
                      <p className="text-white/80 text-sm">{getTicketType(ticket)} TICKET</p>
                    </div>
                    <div className="text-right">
                      <span className={`px-3 py-1.5 rounded-lg text-xs font-bold uppercase border ${getStatusColor(ticket.status)}`}>
                        {ticket.status}
                      </span>
                    </div>
                  </div>

                  <div className="mt-4 pt-4 border-t border-white/20">
                    <p className="text-white/80 text-sm mb-1">Ticket Code</p>
                    <p className="text-xl font-mono font-bold">{ticket.code}</p>
                  </div>
                </div>

                {/* Ticket Body */}
                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Left Column */}
                    <div className="space-y-4">
                      {getTicketType(ticket) === 'EVENT' && ticket.eventId && (
                        <>
                          {ticket.eventId.startsAt && (
                            <div>
                              <div className="flex items-center gap-2 text-slate-600 mb-1">
                                <CalendarIcon className="w-4 h-4" />
                                <span className="text-xs font-semibold">Date & Time</span>
                              </div>
                              <p className="text-slate-900 font-medium text-sm">
                                {new Date(ticket.eventId.startsAt).toLocaleDateString('en-US', {
                                  weekday: 'long',
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric'
                                })}
                              </p>
                              <p className="text-slate-600 text-xs">
                                {new Date(ticket.eventId.startsAt).toLocaleTimeString('en-US', {
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </p>
                            </div>
                          )}

                          {ticket.eventId.venue && (
                            <div>
                              <div className="flex items-center gap-2 text-slate-600 mb-1">
                                <MapPinIcon className="w-4 h-4" />
                                <span className="text-xs font-semibold">Venue</span>
                              </div>
                              <p className="text-slate-900 font-medium text-sm">{ticket.eventId.venue}</p>
                            </div>
                          )}
                        </>
                      )}

                      {getTicketType(ticket) === 'OFFER' && ticket.offerId && (
                        <>
                          {ticket.offerId.validFrom && ticket.offerId.validUntil && (
                            <div>
                              <div className="flex items-center gap-2 text-slate-600 mb-1">
                                <CalendarIcon className="w-4 h-4" />
                                <span className="text-xs font-semibold">Valid Period</span>
                              </div>
                              <p className="text-slate-900 font-medium text-sm">
                                {new Date(ticket.offerId.validFrom).toLocaleDateString()} - {new Date(ticket.offerId.validUntil).toLocaleDateString()}
                              </p>
                            </div>
                          )}
                        </>
                      )}

                      {ticket.ticketTypeId?.name && (
                        <div>
                          <div className="flex items-center gap-2 text-slate-600 mb-1">
                            <TicketIcon className="w-4 h-4" />
                            <span className="text-xs font-semibold">Ticket Type</span>
                          </div>
                          <p className="text-slate-900 font-medium text-sm">{ticket.ticketTypeId.name}</p>
                        </div>
                      )}

                      <div>
                        <div className="flex items-center gap-2 text-slate-600 mb-1">
                          <UserIcon className="w-4 h-4" />
                          <span className="text-xs font-semibold">Attendee</span>
                        </div>
                        <p className="text-slate-900 font-medium text-sm">{ticket.attendeeId?.name || ticket.attendeeId?.email || 'N/A'}</p>
                        {ticket.attendeeId?.email && (
                          <p className="text-slate-600 text-xs">{ticket.attendeeId.email}</p>
                        )}
                      </div>
                    </div>

                    {/* Right Column */}
                    <div className="space-y-4">
                      <div>
                        <div className="flex items-center gap-2 text-slate-600 mb-1">
                          <ClockIcon className="w-4 h-4" />
                          <span className="text-xs font-semibold">Issued On</span>
                        </div>
                        <p className="text-slate-900 font-medium text-sm">
                          {new Date(ticket.issuedAt).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>

                      {ticket.usedAt && (
                        <div>
                          <div className="flex items-center gap-2 text-slate-600 mb-1">
                            <CheckCircleIcon className="w-4 h-4" />
                            <span className="text-xs font-semibold">Used On</span>
                          </div>
                          <p className="text-slate-900 font-medium text-sm">
                            {new Date(ticket.usedAt).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                        </div>
                      )}

                      <div>
                        <div className="flex items-center gap-2 text-slate-600 mb-1">
                          <TicketIcon className="w-4 h-4" />
                          <span className="text-xs font-semibold">Price</span>
                        </div>
                        <p className="text-slate-900 font-bold text-lg">₦{getTicketPrice(ticket).toLocaleString()}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Empty State */}
            {!ticket && !loading && !error && (
              <div className="text-center py-16 bg-white rounded-2xl border border-slate-200">
                <QrCodeIcon className="mx-auto h-16 w-16 text-slate-300 mb-4" />
                <h3 className="text-xl font-semibold text-slate-900 mb-2">Ready to Verify</h3>
                <p className="text-slate-600">Enter a ticket code or scan a QR code to verify</p>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  )
}

export default CenterTicketVerificationPage

